import { TooltipProvider } from "@/components/ui/tooltip";
import { useCallback, useRef } from "react";
import {
  CanvasFloatingToolbarFrame,
  CanvasToolbarDivider,
} from "../components/toolbar";
import { EDGE_COLORS } from "./shared/CanvasColorPalettes";
import { CanvasColorPickerButton } from "./shared/CanvasColorPickerButton";
import { StrokeWidthPicker } from "./shared/LineStylePicker";
import { NodeToolbarContextMenuButton } from "./shared/NodeToolbarContextMenuButton";

export interface DrawingStyle {
  color: string;
  stroke_width: number;
}

interface DrawingFloatingToolbarProps {
  style: DrawingStyle;
  onStyleChange: (style: Partial<DrawingStyle>) => void;
  onPopoverOpenChange?: (open: boolean) => void;
}

/**
 * Selected ink: colour and weight. The tip belongs to the pen, not to a stroke
 * already on the board, so it stays in the draw flyout.
 */
export function DrawingFloatingToolbar({
  style,
  onStyleChange,
  onPopoverOpenChange,
}: DrawingFloatingToolbarProps) {
  const isColorOpenRef = useRef(false);
  const isWidthOpenRef = useRef(false);

  const notifyOpen = useCallback(
    (open: boolean) => {
      onPopoverOpenChange?.(
        isColorOpenRef.current || isWidthOpenRef.current || open,
      );
    },
    [onPopoverOpenChange],
  );

  const handleColorOpen = useCallback(
    (open: boolean) => {
      isColorOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  const handleWidthOpen = useCallback(
    (open: boolean) => {
      isWidthOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  return (
    <TooltipProvider delayDuration={300}>
      <CanvasFloatingToolbarFrame onMouseDown={(e) => e.preventDefault()}>
        <CanvasColorPickerButton
          colors={EDGE_COLORS}
          role="mark"
          activeValue={style.color}
          tooltip="Color"
          onColorChange={(color) => onStyleChange({ color })}
          onOpenChange={handleColorOpen}
          emitField="swatchHex"
        />

        <StrokeWidthPicker
          value={style.stroke_width}
          onChange={(stroke_width) => onStyleChange({ stroke_width })}
          onOpenChange={handleWidthOpen}
        />

        <CanvasToolbarDivider />

        <NodeToolbarContextMenuButton />
      </CanvasFloatingToolbarFrame>
    </TooltipProvider>
  );
}
